import {
  JobConditionAddDialogComponent,
  JobConditions,
} from '@agent-ds/shared/components/job-condition-add-dialog/job-condition-add-dialog.component';
import { NULL_SELECTED_VALUE } from '@agent-ds/shared/constants';
import { JobAzCheckWageErrorDetail } from '@agent-ds/shared/interfaces';
import {
  ALL_GRAD_CATEGORIES,
  GradCategory,
  JOB_AZ_CHECK_CONSULTING_INFO_DYNAMIC_DATA_KEY,
  JobAzCheckConsultingInfo,
  JobAzCheckTiming,
} from '@agent-ds/shared/interfaces/job-az-check';
import {
  clearFixedLateNightOvertimeCategoryValues,
  forceFixedOvertimeFeeNoneForDeemedWorkingHours,
  formatVariableWorkingHoursSystemDescription,
  isDeemedWorkingHoursSystemSelected,
  resolveConditionGradCategories,
} from '../../../../util/job-salary-table';
import { applyNumericInputSanitization } from '@agent-ds/shared/util/numeric-input';
import { buildSalaryTableKey, getGradCategoryLabel } from '@agent-ds/shared/util/salary-table-key';
import { DialogService, DynamicFieldService } from '@agent-ds/shared/services';
import { JobAzCheckService, WageCheckFieldGroup } from '../../services/job-az-check.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface ConsultationInfoSection {
  title: string;
  consultingInfo: JobAzCheckConsultingInfo;
}

const CONSULTATION_INFO_TITLES: { [key in JobAzCheckTiming]: string } = {
  onCreate: '企画相談内容（作成時点）',
  onCompanyCheck: '企業からの相談内容',
  beforeAz: '企画相談内容（AZ前）',
  afterAz1st: '企画相談内容（募集中・企業送信前）',
  afterAz2nd: '企画相談内容（募集中・企業送信後）',
  afterAzOnCompanyCheck: '企業からの相談内容（募集中）',
};

// 画面を表示しているタイミング（checkTiming）ごとに表示する企画相談内容の保存タイミング
const CONSULTATION_INFO_TARGETS_BY_TIMING: { [key in JobAzCheckTiming]: JobAzCheckTiming[] } = {
  onCreate: ['onCreate'],
  onCompanyCheck: [],
  beforeAz: ['onCreate', 'onCompanyCheck', 'beforeAz'],
  afterAz1st: ['onCreate', 'onCompanyCheck', 'beforeAz', 'afterAz1st'],
  afterAz2nd: ['onCreate', 'onCompanyCheck', 'beforeAz', 'afterAz1st', 'afterAzOnCompanyCheck', 'afterAz2nd'],
  afterAzOnCompanyCheck: [],
};

@Component({
  selector: 'ag-job-az-check-step-result',
  templateUrl: './job-az-check-step-result.component.html',
  styleUrls: ['./job-az-check-step-result.component.scss'],
})
export class JobAzCheckStepResultComponent implements OnInit, OnDestroy {
  // チェック結果画面がどのフロー（onCreate/beforeAz/afterAzのダイアログ）から呼び出されたかを表す
  @Input() checkTiming: JobAzCheckTiming = 'onCreate';
  dynamicData: any;
  consultationInfoSections: ConsultationInfoSection[] = [];
  workhourTypeOptions: string[] = [];
  readonly variableWorkingHoursSystemOptions: string[] = ['1週間単位', '1ヶ月単位', '1年単位'];
  readonly getGradCategoryLabel = getGradCategoryLabel;

  private previewPdfBlob: Blob | null = null;
  private previewPdfObjectUrl: string | null = null;
  private previewPdfSafeUrl: SafeResourceUrl | null = null;

  constructor(
    public jobAzCheckService: JobAzCheckService,
    private dynamicFieldService: DynamicFieldService,
    private dialog: DialogService,
    private sanitizer: DomSanitizer,
  ) {}

  get previewPdfUrl(): SafeResourceUrl | null {
    const blob = this.jobAzCheckService.previewPdf;
    if (blob !== this.previewPdfBlob) {
      this.releasePreviewPdfUrl();
      this.previewPdfBlob = blob;
      if (blob) {
        this.previewPdfObjectUrl = URL.createObjectURL(blob);
        this.previewPdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewPdfObjectUrl);
      }
    }
    return this.previewPdfSafeUrl;
  }

  get hasFixedOvertimeFee(): boolean {
    return this.dynamicData.salaryTable.fixedOvertimeFee === '有';
  }

  get hasDeemedWorkingHoursSystem(): boolean {
    return isDeemedWorkingHoursSystemSelected(this.dynamicData.workhourType);
  }

  get workhourType(): string {
    return this.dynamicData.workhourType || NULL_SELECTED_VALUE;
  }

  set workhourType(value: string) {
    this.dynamicData.workhourType = value === NULL_SELECTED_VALUE ? null : value;
    if (!this.hasDeemedWorkingHoursSystem) {
      this.dynamicData.deemedDailyWorkingHours = null;
    } else {
      // みなし労働時間制の有無が「有」から始まる場合、固定残業代は「無」となるため、固定残業代（固定の深夜割増手当分を含む）を「無」に強制してクリアする。
      // '固定の深夜割増手当'を指定することで、クリアに伴う給与補足欄への反映も更新する。
      forceFixedOvertimeFeeNoneForDeemedWorkingHours(this.dynamicData.salaryTable);
      this.jobAzCheckService.refreshSalaryTableRow('固定の深夜割増手当');
    }

    // checkSections側の固定残業代行（一般リスト）の活性/非活性状態を同期する。
    // このパネルの編集はag-dynamic-formのonFormChangedを経由しないため、明示的に呼び出す
    this.jobAzCheckService.syncFixedOvertimeFeeDisabledState();
  }

  get salaryCategoryKeys(): GradCategory[] {
    return this.computeSalaryCategoryKeys();
  }

  get fixedOvertimeCategoryKeys(): GradCategory[] {
    return this.computeFixedOvertimeCategoryKeys();
  }

  get fixedLateNightOvertimeCategoryKeys(): GradCategory[] {
    return this.computeFixedLateNightOvertimeCategoryKeys();
  }

  // 種別なし(noCategory)はGRAD_CATEGORY_OPTIONSに含まれずラベルが空になるため、表示用に「種別なし」を補う
  getDisplayGradCategoryLabel(category: GradCategory): string {
    return getGradCategoryLabel(category) || '種別なし';
  }

  getCalculationStepsTitle(group: WageCheckFieldGroup, detail: JobAzCheckWageErrorDetail): string {
    const gradCategoryLabel = detail.gradCategory ? `・${this.getDisplayGradCategoryLabel(detail.gradCategory)}` : '';
    return `計算式（${group.label}${gradCategoryLabel}）`;
  }

  ngOnInit(): void {
    this.dynamicData = this.jobAzCheckService.job.dynamicData;
    this.consultationInfoSections = this.buildConsultationInfoSections();
    this.initWageCheckDefaults();
    this.workhourTypeOptions = this.getWorkhourTypeOptions();
  }

  private buildConsultationInfoSections(): ConsultationInfoSection[] {
    const targets = CONSULTATION_INFO_TARGETS_BY_TIMING[this.checkTiming];

    const sections: ConsultationInfoSection[] = [];
    targets.forEach((timing) => {
      const consultingInfo: JobAzCheckConsultingInfo | undefined = this.dynamicData[JOB_AZ_CHECK_CONSULTING_INFO_DYNAMIC_DATA_KEY[timing]];
      if (this.hasConsultation(consultingInfo)) {
        sections.push({
          title: CONSULTATION_INFO_TITLES[timing],
          consultingInfo: { ...consultingInfo, savedTiming: timing },
        });
      }
    });

    // 表示対象が1件のみの場合は共通タイトルで表示する
    if (sections.length === 1) {
      sections[0].title = '企画相談内容';
    }

    return sections;
  }

  private hasConsultation(consultingInfo: JobAzCheckConsultingInfo | undefined): consultingInfo is JobAzCheckConsultingInfo {
    return !!consultingInfo && (!!consultingInfo.contactMessage || Object.keys(consultingInfo.consultingItems || {}).length > 0);
  }

  /**
   * 企業からの相談内容（作成〜AZ前ルートのonCompanyCheck、募集中ルートのafterAzOnCompanyCheck）かどうか
   */
  isCompanyConsultation(savedTiming: JobAzCheckTiming): boolean {
    return savedTiming === 'onCompanyCheck' || savedTiming === 'afterAzOnCompanyCheck';
  }

  onVariableWorkingHoursSystemChange(value: string): void {
    this.applyVariableWorkingHoursSystemConditions(value);
  }

  removeSalaryCategory(category: GradCategory): void {
    this.dynamicData.salaryTable[buildSalaryTableKey('salary_', category)] = null;
    this.onFormChanged('月給');
  }

  removeFixedOvertimeCategory(category: GradCategory): void {
    this.dynamicData.salaryTable[buildSalaryTableKey('fixedOvertimeHours_', category)] = null;
    this.dynamicData.salaryTable[buildSalaryTableKey('fixedOvertimeAmount_', category)] = null;
    this.onFormChanged('固定残業代補足');
  }

  removeFixedLateNightOvertimeCategory(category: GradCategory): void {
    this.dynamicData.salaryTable[buildSalaryTableKey('fixedLateNightOvertimeHours_', category)] = null;
    this.dynamicData.salaryTable[buildSalaryTableKey('fixedLateNightOvertimeAmount_', category)] = null;
    if (this.fixedLateNightOvertimeCategoryKeys.length === 0) {
      this.dynamicData.salaryTable.fixedLateNightOvertimeType = null;
      clearFixedLateNightOvertimeCategoryValues(this.dynamicData.salaryTable);
    }
    this.onFormChanged('固定の深夜割増手当');
  }

  removeOtherConditionWithKey(key: string): void {
    if (key === 'variableWorkingHoursSystem') {
      this.applyVariableWorkingHoursSystemConditions(null);
      return;
    }
    this.onFormChanged();
    this.dynamicData[key] = null;
  }

  addSalaryPattern(): void {
    const config = {
      data: {
        conditionKey: 'salary',
        salaryTable: this.dynamicData.salaryTable,
      },
    };

    this.dialog.open(JobConditionAddDialogComponent, config, (conditions: JobConditions) => {
      if (!conditions) {
        return;
      }
      this.applySalaryConditions(conditions);
    });
  }

  addFixedOvertimePattern(): void {
    const config = {
      data: {
        conditionKey: 'fixedOvertime',
        salaryTable: this.dynamicData.salaryTable,
      },
    };

    this.dialog.open(JobConditionAddDialogComponent, config, (conditions: JobConditions) => {
      if (!conditions) {
        return;
      }
      this.applyFixedOvertimeConditions(conditions);
    });
  }

  addFixedLateNightOvertimePattern(): void {
    const config = {
      data: {
        conditionKey: 'fixedLateNightOvertime',
        salaryTable: this.dynamicData.salaryTable,
      },
    };

    this.dialog.open(JobConditionAddDialogComponent, config, (conditions: JobConditions) => {
      if (!conditions) {
        return;
      }
      this.applyFixedLateNightOvertimeConditions(conditions);
    });
  }

  addOtherCondition(): void {
    const config = {
      data: {
        conditionKey: 'other',
        variableWorkingHoursSystemOptions: this.variableWorkingHoursSystemOptions,
        salaryTable: this.dynamicData.salaryTable,
      },
    };

    this.dialog.open(JobConditionAddDialogComponent, config, (conditions: JobConditions) => {
      if (!conditions) {
        return;
      }

      if (conditions.monthlyScheduledWorkingHours) {
        this.dynamicData.monthlyScheduledWorkingHours = conditions.monthlyScheduledWorkingHours;
        this.onFormChanged();
        
      }

      if (conditions.annualScheduledWorkingHours) {
        this.dynamicData.annualScheduledWorkingHours = conditions.annualScheduledWorkingHours;
        this.onFormChanged();
      }

      if (conditions.variableWorkingHoursSystem) {
        this.applyVariableWorkingHoursSystemConditions(conditions.variableWorkingHoursSystem);
      }

      this.applyFixedLateNightOvertimeConditions(conditions);
    });
  }

  onFormChanged(salaryTableRowTitle?: string): void {
    this.jobAzCheckService.hasUnsavedChanges = true;
    if (salaryTableRowTitle) {
      // salaryTable 関連の修正があった場合カラム別側の表示も更新する
      this.jobAzCheckService.refreshSalaryTableRow(salaryTableRowTitle);
    }
  }

  onFormChangedWithApplyNumericInput(event: Event, key: string, target: any = this.dynamicData, formChangedTitle?: string): void {
    target[key] = applyNumericInputSanitization(event);
    this.onFormChanged(formChangedTitle);
  }

  private applySalaryConditions(conditions: JobConditions): void {
    if (conditions.amount == null) {
      return;
    }

    resolveConditionGradCategories(conditions).forEach((category) => {
      this.dynamicData.salaryTable[buildSalaryTableKey('salary_', category)] = conditions.amount;
    });

    this.onFormChanged('月給');
  }

  private applyFixedOvertimeConditions(conditions: JobConditions): void {
    if (conditions.amount == null || conditions.hours == null) {
      return;
    }

    resolveConditionGradCategories(conditions).forEach((category) => {
      this.dynamicData.salaryTable[buildSalaryTableKey('fixedOvertimeHours_', category)] = conditions.hours;
      this.dynamicData.salaryTable[buildSalaryTableKey('fixedOvertimeAmount_', category)] = conditions.amount;
    });

    this.onFormChanged('固定残業代補足');
  }

  private applyFixedLateNightOvertimeConditions(conditions: JobConditions): void {
    if (conditions.amount == null || conditions.hours == null) {
      return;
    }

    resolveConditionGradCategories(conditions).forEach((category) => {
      this.dynamicData.salaryTable[buildSalaryTableKey('fixedLateNightOvertimeHours_', category)] = conditions.hours;
      this.dynamicData.salaryTable[buildSalaryTableKey('fixedLateNightOvertimeAmount_', category)] = conditions.amount;
    });

    this.dynamicData.salaryTable.fixedLateNightOvertimeType = '有';
    this.onFormChanged('固定の深夜割増手当');
  }

  private applyVariableWorkingHoursSystemConditions(value: string | null): void {
    this.dynamicData.salaryTable.variableWorkingHoursSystem = value;
    this.dynamicData.salaryTable.salaryDescriptionVariableWorkingHoursSystem = formatVariableWorkingHoursSystemDescription(value);
    this.onFormChanged('変形労働時間制');
  }

  private initWageCheckDefaults(): void {
    // 就業時間
    if (!this.dynamicData.workingHourRange) {
      this.dynamicData.workingHourRange = { from: null, to: null };
    }

    // 月給・固定残業・固定深夜割増
    if (!this.dynamicData.salaryTable) {
      this.dynamicData.salaryTable = {};
    }

    // 勤務地_都道府県
    if (!this.dynamicData.workplacePrefectures) {
      this.dynamicData.workplacePrefectures = [];
    }
  }

  private computeSalaryCategoryKeys(): GradCategory[] {
    return ALL_GRAD_CATEGORIES.filter((category) => this.dynamicData.salaryTable[buildSalaryTableKey('salary_', category)] != null);
  }

  private computeFixedOvertimeCategoryKeys(): GradCategory[] {
    return this.computeCategoryKeysWithBothFields('fixedOvertimeHours_', 'fixedOvertimeAmount_');
  }

  private computeFixedLateNightOvertimeCategoryKeys(): GradCategory[] {
    return this.computeCategoryKeysWithBothFields('fixedLateNightOvertimeHours_', 'fixedLateNightOvertimeAmount_');
  }

  private computeCategoryKeysWithBothFields(
    hoursPrefix: 'fixedOvertimeHours_' | 'fixedLateNightOvertimeHours_',
    amountPrefix: 'fixedOvertimeAmount_' | 'fixedLateNightOvertimeAmount_',
  ): GradCategory[] {
    return ALL_GRAD_CATEGORIES.filter(
      (category) =>
        this.dynamicData.salaryTable[buildSalaryTableKey(hoursPrefix, category)] != null &&
        this.dynamicData.salaryTable[buildSalaryTableKey(amountPrefix, category)] != null
    );
  }

  private getWorkhourTypeOptions(): string[] {
    const def = this.dynamicFieldService.getDefinition('job', 'workhourType');
    if (!def || !def.validationStyle || !def.validationStyle.options) {
      return [];
    }
    return [NULL_SELECTED_VALUE, ...def.validationStyle.options];
  }

  ngOnDestroy(): void {
    this.releasePreviewPdfUrl();
  }

  private releasePreviewPdfUrl(): void {
    if (this.previewPdfObjectUrl) {
      URL.revokeObjectURL(this.previewPdfObjectUrl);
    }
    this.previewPdfObjectUrl = null;
    this.previewPdfSafeUrl = null;
  }
}
